import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Droplets, 
  Wallet, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  Info,
  ArrowRight,
  Coins,
  Users,
  Activity
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import LOGO from '../assets/logo180.png';

const Container = styled.div`
  background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
  min-height: 100vh;
  color: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Header = styled.header`
  padding: 1rem 2rem;
  background: rgba(11, 20, 38, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
`;

const LogoIcon = styled.img`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: #94A3B8;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  
  &:hover {
    color: #3B82F6;
  }
  
  &.active {
    color: #3B82F6;
  }
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: #94A3B8;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 4rem 2rem 2rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin: 0 0 1rem 0;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroHighlight = styled.span`
  background: linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1.125rem;
  color: #94A3B8;
  margin: 0 0 2rem 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const MainContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem 4rem 2rem;
`;

const FaucetCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 3rem;
  backdrop-filter: blur(10px);
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(59, 130, 246, 0.3);
    box-shadow: 0 20px 40px rgba(59, 130, 246, 0.1);
  }
`;

const FaucetIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem auto;
  box-shadow: 0 0 30px rgba(59, 130, 246, 0.3);
`;

const TokenSelector = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 0.5rem;
  background: rgba(30, 41, 59, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const TokenOption = styled.button`
  flex: 1;
  padding: 1rem;
  background: ${props => props.selected ? 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)' : 'transparent'};
  border: none;
  border-radius: 8px;
  color: ${props => props.selected ? '#ffffff' : '#94A3B8'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.selected 
      ? 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)' 
      : 'rgba(255, 255, 255, 0.05)'};
    color: #ffffff;
  }
`;

const TokenAmount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #3B82F6;
  margin-bottom: 2rem;
`;

const AddressInput = styled.input`
  width: 100%;
  padding: 1rem;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #ffffff;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #64748B;
  }
`;

const RequestButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const InfoIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor};
`;

const InfoTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const InfoContent = styled.div`
  color: #94A3B8;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const InfoValue = styled.div`
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 0.5rem;
`;

const AlertBox = styled.div`
  background: ${props => props.type === 'warning' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  border: 1px solid ${props => props.type === 'warning' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(59, 130, 246, 0.3)'};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const AlertIcon = styled.div`
  color: ${props => props.type === 'warning' ? '#F59E0B' : '#3B82F6'};
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const AlertContent = styled.div`
  color: #94A3B8;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const AlertTitle = styled.div`
  color: ${props => props.type === 'warning' ? '#F59E0B' : '#3B82F6'};
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const RecentRequests = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(10px);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor};
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const RequestItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const RequestInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const RequestAddress = styled.div`
  color: #3B82F6;
  font-weight: 600;
  font-size: 0.875rem;
`;

const RequestTime = styled.div`
  color: #64748B;
  font-size: 0.75rem;
`;

const RequestStatus = styled.div`
  background: ${props => props.status === 'completed' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'};
  color: ${props => props.status === 'completed' ? '#10B981' : '#F59E0B'};
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const RequestAmount = styled.div`
  color: #ffffff;
  font-weight: 600;
  text-align: right;
  font-size: 0.875rem;
`;

const TestnetFaucet = () => {
  const [walletAddress, setWalletAddress] = useState('');
  const [selectedToken, setSelectedToken] = useState('HYX'); // 'HYX' or 'USDH'
  const [isRequesting, setIsRequesting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'
  const [recentRequests, setRecentRequests] = useState([]);
  const [dailyStats, setDailyStats] = useState({ requests: 247 });
  const navigate = useNavigate();

  // Your faucet server URL - replace with your actual domain
  const FAUCET_API_URL = 'https://chainfree.info:5003';

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleRequest = async () => {
    if (!walletAddress) return;
    
    setIsRequesting(true);
    setMessage('');
    setMessageType('');
    
    try {
      // Choose endpoint based on selected token
      const endpoint = selectedToken === 'HYX' 
        ? `${FAUCET_API_URL}/faucet/claim`
        : `${FAUCET_API_URL}/faucet/claim-usdh`;
      
      const response = await axios.post(endpoint, {
        address: walletAddress
      });

      if (response.data.success) {
        setMessage(`🎉 Success! ${response.data.amount} sent to your wallet!`);
        setMessageType('success');
        setWalletAddress(''); // Clear input
        
        // Add to recent requests
        const newRequest = {
          address: walletAddress,
          amount: response.data.amount,
          time: 'Just now',
          status: 'completed',
          txHash: response.data.txHash
        };
        setRecentRequests(prev => [newRequest, ...prev.slice(0, 4)]);
        
        // Auto-clear success message after 10 seconds
        setTimeout(() => {
          setMessage('');
          setMessageType('');
        }, 10000);
        
      } else {
        setMessage(`❌ ${response.data.error || 'Request failed'}`);
        setMessageType('error');
      }
    } catch (error) {
      console.error('Faucet request error:', error);
      
      if (error.response?.data?.error) {
        setMessage(`❌ ${error.response.data.error}`);
      } else {
        setMessage('❌ Network error. Please try again.');
      }
      setMessageType('error');
    }
    
    setIsRequesting(false);
  };

  // Load recent requests on component mount
  useEffect(() => {
    // Initialize with some mock data since we don't have a history endpoint
    const mockRecentRequests = [
      {
        address: '0x742d35Cc6634C0532925a3b8D8c35ccC8c19fAEd',
        amount: '10 HYX',
        time: '2 minutes ago',
        status: 'completed'
      },
      {
        address: '0x8ba1f109551bD432803012645Hac136c9c9A',
        amount: '10 USDH',
        time: '5 minutes ago',
        status: 'completed'
      }
    ];
    setRecentRequests(mockRecentRequests);
  }, []);

  return (
    <Container>
      <Header>
        <Nav>
          <Logo onClick={handleBackToHome}>
            <LogoIcon src={LOGO} />
            Hydrixscan
          </Logo>
          <NavLinks>
            <NavLink href="#">Blockchain</NavLink>
            <NavLink href="#">Tokens</NavLink>
            <NavLink href="#">NFTs</NavLink>
            <NavLink href="#">Resources</NavLink>
            <NavLink href="#" className="active">Faucet</NavLink>
          </NavLinks>
          <BackButton onClick={handleBackToHome}>← Back to Explorer</BackButton>
        </Nav>
      </Header>

      <HeroSection>
        <HeroTitle>
          Testnet <HeroHighlight>Faucet</HeroHighlight>
        </HeroTitle>
        <HeroSubtitle>
          Get free testnet tokens for development and testing purposes. No mainnet value.
        </HeroSubtitle>
      </HeroSection>

      <MainContent>
        <AlertBox type="info">
          <AlertIcon type="info">
            <Info size={20} />
          </AlertIcon>
          <div>
            <AlertTitle type="info">Testnet Tokens Only</AlertTitle>
            <AlertContent>
              These tokens are for testing purposes only and have no monetary value. 
              Do not send mainnet transactions to these addresses.
            </AlertContent>
          </div>
        </AlertBox>

        <FaucetCard>
          <FaucetIcon>
            <Droplets size={40} color="white" />
          </FaucetIcon>
          
          <TokenSelector>
            <TokenOption 
              selected={selectedToken === 'HYX'}
              onClick={() => setSelectedToken('HYX')}
            >
              <Coins size={20} />
              HYX Token
            </TokenOption>
            <TokenOption 
              selected={selectedToken === 'USDH'}
              onClick={() => setSelectedToken('USDH')}
            >
              <Wallet size={20} />
              USDH Stablecoin
            </TokenOption>
          </TokenSelector>
          
          <TokenAmount>
            <Coins size={24} />
            10 {selectedToken} per request
          </TokenAmount>

          <AddressInput
            type="text"
            placeholder="Enter your wallet address (0x...)"
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
          />

          {message && (
            <div style={{ 
                padding: '1rem 1.5rem',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                background: messageType === 'success' 
                ? 'rgba(16, 185, 129, 0.15)' 
                : 'rgba(239, 68, 68, 0.15)',
                border: `2px solid ${messageType === 'success' ? '#10B981' : '#EF4444'}`,
                color: messageType === 'success' ? '#10B981' : '#EF4444',
                fontSize: '1rem',
                fontWeight: '600',
                textAlign: 'center',
                animation: 'fadeIn 0.3s ease-in'
            }}>
                {message}
                {messageType === 'success' && (
                <div style={{ 
                    fontSize: '0.875rem', 
                    marginTop: '0.5rem',
                    opacity: '0.8' 
                }}>
                    Check your MetaMask wallet - tokens should appear shortly!
                </div>
                )}
            </div>
          )}

          <RequestButton 
            onClick={handleRequest}
            disabled={!walletAddress || isRequesting}
          >
            {isRequesting ? (
              <>
                <Activity size={20} className="animate-spin" />
                Processing Request...
              </>
            ) : (
              <>
                Request {selectedToken} Tokens
                <ArrowRight size={20} />
              </>
            )}
          </RequestButton>
        </FaucetCard>

        <InfoGrid>
          <InfoCard>
            <InfoHeader>
              <InfoIcon color="rgba(59, 130, 246, 0.2)" iconColor="#3B82F6">
                <Clock size={18} />
              </InfoIcon>
              <InfoTitle>Rate Limit</InfoTitle>
            </InfoHeader>
            <InfoContent>
              Independent 24-hour cooldown for each token type
            </InfoContent>
            <InfoValue>24h per token</InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoHeader>
              <InfoIcon color="rgba(6, 182, 212, 0.2)" iconColor="#06B6D4">
                <Wallet size={18} />
              </InfoIcon>
              <InfoTitle>Token Amount</InfoTitle>
            </InfoHeader>
            <InfoContent>
              Fixed amount per successful request
            </InfoContent>
            <InfoValue>10 HYX / 10 USDH</InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoHeader>
              <InfoIcon color="rgba(16, 185, 129, 0.2)" iconColor="#10B981">
                <Users size={18} />
              </InfoIcon>
              <InfoTitle>Daily Requests</InfoTitle>
            </InfoHeader>
            <InfoContent>
              Total requests processed today
            </InfoContent>
            <InfoValue>247 requests</InfoValue>
          </InfoCard>
        </InfoGrid>

        <AlertBox type="warning">
          <AlertIcon type="warning">
            <AlertTriangle size={20} />
          </AlertIcon>
          <div>
            <AlertTitle type="warning">Important Notes</AlertTitle>
            <AlertContent>
              • Only use valid Ethereum-compatible addresses<br/>
              • HYX and USDH have separate 24-hour cooldowns - you can claim both!<br/>
              • Requests are processed automatically within 1-2 minutes<br/>
              • If you don't receive tokens, check your address and try again after 24 hours<br/>
              • Report any issues through our support channels
            </AlertContent>
          </div>
        </AlertBox>

        <RecentRequests>
          <SectionHeader>
            <SectionIcon color="rgba(6, 182, 212, 0.2)" iconColor="#06B6D4">
              <Activity size={18} />
            </SectionIcon>
            <SectionTitle>Recent Requests</SectionTitle>
          </SectionHeader>
          
          {recentRequests.map((request, index) => (
            <RequestItem key={index}>
              <RequestInfo>
                <RequestAddress>
                  {request.address.slice(0, 6)}...{request.address.slice(-4)}
                </RequestAddress>
                <RequestTime>{request.time}</RequestTime>
              </RequestInfo>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                <RequestStatus status={request.status}>
                  {request.status}
                </RequestStatus>
                <RequestAmount>{request.amount}</RequestAmount>
              </div>
            </RequestItem>
          ))}
        </RecentRequests>
       
      </MainContent>
    </Container>
  );
};

export default TestnetFaucet;